import { TimelineShape } from '../TimelineSchedule';
import * as maptilersdk from '@maptiler/sdk';
import { EngagementFieldsFragment } from '@flashpack/graphql';
import {
  createLineGeoJSON,
  createArrowSymbolsGeoJSON,
  createEngagementLabelGeoJSON,
  createAccommodationLabelGeoJSON,
  createEngagementMarkersGeoJSON,
  createAccommodationMarkersGeoJSON,
} from './geoJsonHelpers';

// Define types for the source and layer IDs
export type SourceId = string;
export type LayerId = string;
export type MapState = {
  markers: maptilersdk.Marker[];
  lineSources: SourceId[];
  lineLayers: LayerId[];
  labelSources: SourceId[];
  labelLayers: LayerId[];
  isPersistentElementsDrawn: boolean; // Track if lines and markers are already drawn
};

// Clear only dynamic elements (labels and engagement markers) to prevent flickering
export const clearDynamicMapElements = (
  map: React.RefObject<maptilersdk.Map>,
  mapState: MapState,
  setMapState: (state: MapState) => void,
) => {
  const { labelSources, labelLayers } = mapState;

  // Clear existing label sources and layers (which now include engagement markers)
  labelSources.forEach((sourceId) => {
    if (map.current && map.current.getSource(sourceId)) {
      const layers = labelLayers.filter(
        (layerId) => map.current?.getLayer(layerId) && map.current?.getSource(sourceId),
      );
      layers.forEach((layerId) => {
        if (map.current?.getLayer(layerId)) {
          map.current.removeLayer(layerId);
        }
      });
      map.current.removeSource(sourceId);
    }
  });

  setMapState({
    ...mapState,
    labelSources: [],
    labelLayers: [],
    isPersistentElementsDrawn: true,
  });
};

// Draw persistent elements (lines and accommodation markers) - only called once during initialization
export const drawPersistentMapFeatures = (
  map: React.RefObject<maptilersdk.Map>,
  timeline: TimelineShape,
  mapState: MapState,
  setMapState: (state: MapState) => void,
) => {
  if (!map.current || mapState.isPersistentElementsDrawn) {
    return;
  }

  const newMarkers: maptilersdk.Marker[] = [];
  const newLineSources: SourceId[] = [];
  const newLineLayers: LayerId[] = [];

  try {
    const bounds = new maptilersdk.LngLatBounds();

    // Get accommodations and process them
    const allAccommodations = collectAccommodationsWithDays(timeline);

    // Create accommodation route lines
    if (allAccommodations.length > 1 && map.current) {
      // Get coordinates for the route based on day-by-day progression
      const accommodationCoordinates = createAccommodationRouteCoordinates(timeline);

      // Only proceed if we have at least 2 points to draw a line
      if (accommodationCoordinates.length >= 2) {
        // Create lines
        const lineSourceId = 'accommodation-line-source';
        const lineLayerId = 'accommodation-line-layer';

        // Check if source already exists, if not add it
        if (!map.current.getSource(lineSourceId)) {
          map.current.addSource(lineSourceId, {
            type: 'geojson',
            data: createLineGeoJSON(accommodationCoordinates),
          });
        }

        // Check if layer already exists, if not add it
        if (!map.current.getLayer(lineLayerId)) {
          map.current.addLayer({
            id: lineLayerId,
            type: 'line',
            source: lineSourceId,
            paint: {
              'line-width': 2,
              'line-color': '#5A5BBB',
            },
          });
        }

        // Create arrow symbols
        const arrowSourceId = 'accommodation-arrow-source';
        const arrowLayerId = 'accommodation-arrow-layer';

        // Check if arrow source already exists, if not add it
        if (!map.current.getSource(arrowSourceId)) {
          map.current.addSource(arrowSourceId, {
            type: 'geojson',
            data: createArrowSymbolsGeoJSON(accommodationCoordinates),
          });
        }

        // Check if arrow layer already exists, if not add it
        if (!map.current.getLayer(arrowLayerId)) {
          map.current.addLayer({
            id: arrowLayerId,
            type: 'symbol',
            source: arrowSourceId,
            layout: {
              'icon-image': 'arrow-icon',
              'icon-size': 0.5,
              'icon-rotate': ['get', 'angle'],
              'icon-rotation-alignment': 'map',
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
            },
          });
        }

        newLineSources.push(lineSourceId, arrowSourceId);
        newLineLayers.push(lineLayerId, arrowLayerId);
      }
    }

    // Add persistent accommodation markers (no flickering, always on top)
    addPersistentAccommodationMarkers(
      allAccommodations,
      map,
      newLineSources,
      newLineLayers,
      bounds,
    );

    // Update map state with persistent elements
    setMapState({
      ...mapState,
      markers: [...mapState.markers, ...newMarkers],
      lineSources: [...mapState.lineSources, ...newLineSources],
      lineLayers: [...mapState.lineLayers, ...newLineLayers],
      isPersistentElementsDrawn: true,
    });
  } catch (error) {
    console.error('Error drawing persistent map features:', error);
  }
};

// Draw dynamic elements (engagement markers and labels) - called when selection changes
export const drawDynamicMapFeatures = (
  map: React.RefObject<maptilersdk.Map>,
  timeline: TimelineShape,
  selectedDay: number | null,
  mapState: MapState,
  setMapState: (state: MapState) => void,
) => {
  if (!map.current) {
    return;
  }

  const newLabelSources: SourceId[] = [];
  const newLabelLayers: LayerId[] = [];

  try {
    const bounds = new maptilersdk.LngLatBounds();
    const allAccommodations = collectAccommodationsWithDays(timeline);

    // Add engagement markers (will be inserted below accommodation markers)
    const engagements = getAllEngagements(timeline);
    addEngagementMarkers(
      engagements,
      selectedDay,
      map,
      newLabelSources,
      newLabelLayers,
      bounds,
    );

    // Add accommodation labels (dynamic, based on selection)
    addAccommodationLabels(
      selectedDay,
      map,
      false, // isCompactView - set to false for dialog view
      newLabelSources,
      newLabelLayers,
      allAccommodations,
    );

    // Add engagement labels for selected day
    addEngagementLabels(
      selectedDay,
      timeline,
      map,
      false, // isCompactView - set to false for dialog view
      newLabelSources,
      newLabelLayers,
    );

    // Set appropriate zoom level
    if (map.current) {
      if (selectedDay) {
        // Create day-specific bounds
        const dayBounds = new maptilersdk.LngLatBounds();
        const dayAccommodations = allAccommodations.filter((a) =>
          a.daysActive.includes(selectedDay),
        );
        const dayEngagements = getEngagementsForDay(selectedDay, timeline);

        // Add day locations to bounds
        dayAccommodations.forEach((a) => {
          if (a.location?.longitude && a.location?.latitude) {
            dayBounds.extend([
              parseFloat(String(a.location.longitude)),
              parseFloat(String(a.location.latitude)),
            ]);
          }
        });
        dayEngagements.forEach((e) => {
          if (e.startMapLocation?.longitude && e.startMapLocation?.latitude) {
            dayBounds.extend([
              parseFloat(e.startMapLocation.longitude.toString()),
              parseFloat(e.startMapLocation.latitude.toString()),
            ]);
          }
        });

        if (!dayBounds.isEmpty()) {
          map.current.fitBounds(dayBounds, {
            padding: {
              top: 200,
              right: 100,
              bottom: 300,
              left: 100,
            },
            maxZoom: 10,
            duration: 2000,
          });
        }
      } else {
        // Overview - fit to all bounds
        // Get all engagements and accommodations for bounds
        const engagements = getAllEngagements(timeline);
        engagements.forEach((engagement) => {
          if (
            engagement.startMapLocation?.longitude &&
            engagement.startMapLocation?.latitude
          ) {
            bounds.extend([
              parseFloat(String(engagement.startMapLocation.longitude)),
              parseFloat(String(engagement.startMapLocation.latitude)),
            ]);
          }
        });
        allAccommodations.forEach((accommodation) => {
          if (accommodation.location?.longitude && accommodation.location?.latitude) {
            bounds.extend([
              parseFloat(accommodation.location.longitude.toString()),
              parseFloat(accommodation.location.latitude.toString()),
            ]);
          }
        });

        const detailedPadding = { top: 100, right: 140, bottom: 150, left: 140 };

        if (!bounds.isEmpty()) {
          map.current.fitBounds(bounds, {
            padding: detailedPadding,
            maxZoom: 11,
            duration: 2000,
            essential: true,
          });
        }
      }
    }

    // Update state with new labels and engagement markers
    setMapState({
      ...mapState,
      labelSources: newLabelSources,
      labelLayers: newLabelLayers,
    });
  } catch (error) {
    console.error('Error drawing dynamic map features:', error);
  }
};

// paints the map features (updated to use new approach)
export const drawMapFeatures = (
  map: React.RefObject<maptilersdk.Map>,
  timeline: TimelineShape,
  selectedDay: number | null,
  setMapState: (state: MapState) => void,
  mapState: MapState,
) => {
  if (!map.current) {
    return;
  }

  // Draw persistent elements only once
  if (!mapState.isPersistentElementsDrawn) {
    drawPersistentMapFeatures(map, timeline, mapState, setMapState);
  }

  // Always clear and redraw dynamic elements (labels)
  clearDynamicMapElements(map, mapState, setMapState);

  // Get updated map state after clearing dynamic elements
  const updatedMapState = {
    ...mapState,
    labelSources: [],
    labelLayers: [],
  };

  drawDynamicMapFeatures(map, timeline, selectedDay, updatedMapState, setMapState);
};

// Create SVG arrow image
export const createArrowImageForMap = (map: React.RefObject<maptilersdk.Map>) => {
  const arrowSvg = `<svg width="5" height="8" viewBox="0 0 5 8" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M0.758737 7.426L4.123 3.84371L0.503906 0.482422" stroke="#5A5BBB" stroke-width="1.23783" stroke-miterlimit="10"/>
</svg>
`;

  const svgImage = new Image(14, 18);
  svgImage.onload = () => {
    if (map.current) {
      map.current.addImage('arrow-icon', svgImage);
    }
  };
  // Convert SVG string to image source
  svgImage.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(arrowSvg);
};

// Interface to track accommodations across multiple days
export interface AccommodationWithDays {
  id: string;
  title: string;
  location: {
    name: string;
    latitude: number;
    longitude: number;
  };
  daysActive: number[];
  firstDayNumber: number;
  checkIn: {
    day: number;
    time: string;
  };
  checkOut: {
    day: number;
    time: string;
  };
}

// Collect accommodations that appear across multiple days from timeline structure
const collectAccommodationsWithDays = (
  timeline: TimelineShape,
): AccommodationWithDays[] => {
  const accommodationsMap = new Map<string, AccommodationWithDays>();

  // Group accommodations by title (hotel name) - only those with location data
  timeline.accommodations
    .filter(
      (accommodation) =>
        accommodation.location?.latitude && accommodation.location?.longitude,
    )
    .forEach((accommodation) => {
      const title = accommodation.title || 'Unknown Hotel';
      const existingAccommodation = accommodationsMap.get(title);

      if (existingAccommodation) {
        // Add days between check-in and check-out
        for (
          let day = accommodation.checkIn.day;
          day < accommodation.checkOut.day;
          day++
        ) {
          if (!existingAccommodation.daysActive.includes(day)) {
            existingAccommodation.daysActive.push(day);
          }
        }
      } else {
        // Create new accommodation with days tracking
        const daysActive: number[] = [];
        for (
          let day = accommodation.checkIn.day;
          day < accommodation.checkOut.day;
          day++
        ) {
          daysActive.push(day);
        }

        accommodationsMap.set(title, {
          id: accommodation.id,
          title,
          location: {
            name: accommodation.location!.name || '',
            latitude: Number(accommodation.location!.latitude),
            longitude: Number(accommodation.location!.longitude),
          },
          daysActive,
          firstDayNumber: accommodation.checkIn.day,
          checkIn: accommodation.checkIn,
          checkOut: accommodation.checkOut,
        });
      }
    });

  return Array.from(accommodationsMap.values());
};

// Create coordinates for routes between accommodations based on day-by-day progression
export const createAccommodationRouteCoordinates = (
  timeline: TimelineShape,
): [number, number][] => {
  // Sort accommodations by check-in day
  const sortedAccommodations = [...timeline.accommodations].sort(
    (a, b) => a.checkIn.day - b.checkIn.day,
  );

  const routeCoordinates: [number, number][] = [];
  let lastAccommodationTitle: string | null = null;

  sortedAccommodations.forEach((accommodation) => {
    const title = accommodation.title || 'Unknown Hotel';

    // If this is a different accommodation and has location data
    if (
      lastAccommodationTitle !== title &&
      accommodation.location?.longitude &&
      accommodation.location?.latitude
    ) {
      routeCoordinates.push([
        parseFloat(accommodation.location.longitude.toString()),
        parseFloat(accommodation.location.latitude.toString()),
      ]);
      lastAccommodationTitle = title;
    }
  });

  return routeCoordinates;
};

// Define EngagementWithDayNumber interface
interface EngagementWithDayNumber extends EngagementFieldsFragment {
  dayNumber: number;
}

// Get array of all engagements from timeline
export const getAllEngagements = (timeline: TimelineShape): EngagementWithDayNumber[] => {
  return timeline.engagements
    .filter((engagement) => engagement.activity?.includedOnWebsiteItinerary)
    .map((engagement) => ({
      ...engagement,
      dayNumber: engagement.start.time.day,
    }));
};

// Get engagements for a specific day
export const getEngagementsForDay = (dayNumber: number, timeline: TimelineShape) => {
  return timeline.engagements.filter(
    (engagement) =>
      engagement.start.time.day === dayNumber &&
      engagement.activity?.includedOnWebsiteItinerary,
  );
};

const addEngagementLabels = (
  selectedDay: number | null,
  timeline: TimelineShape,
  map: React.RefObject<maptilersdk.Map>,
  isCompactView: boolean,
  newLabelSources: SourceId[],
  newLabelLayers: LayerId[],
) => {
  // Add engagement labels for selected day
  if (selectedDay && map.current) {
    const dayEngagements = getEngagementsForDay(selectedDay, timeline);

    if (dayEngagements.length > 0) {
      const engagementLabelSourceId = 'engagement-labels-source';
      const engagementLabelLayerId = 'engagement-labels-layer';

      const dayEngagementsWithDayNumber = dayEngagements.map((engagement) => ({
        ...engagement,
        dayNumber: engagement.start.time.day,
      }));

      map.current.addSource(engagementLabelSourceId, {
        type: 'geojson',
        data: createEngagementLabelGeoJSON(dayEngagementsWithDayNumber),
      });

      map.current.addLayer({
        id: engagementLabelLayerId,
        type: 'symbol',
        source: engagementLabelSourceId,
        layout: {
          'text-field': ['get', 'title'],
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': isCompactView ? 12 : 14,
          'text-offset': [0, 1.5],
          'text-radial-offset': 1.5,
          'symbol-avoid-edges': true,
          'text-justify': 'auto',
          'text-ignore-placement': false,
          'text-allow-overlap': false,
          'icon-allow-overlap': false,
          'icon-ignore-placement': true,
          'symbol-sort-key': 4,
          'text-variable-anchor': [
            'top',
            'bottom',
            'left',
            'right',
            'bottom-left',
            'bottom-right',
            'top-left',
            'top-right',
          ],
          'text-padding': 5,
        },
        paint: {
          'text-color': '#333',
          'text-halo-color': 'rgba(225, 225, 225, 0.4)',
          'text-halo-width': 20,
          'text-halo-blur': 200,
        },
      });

      newLabelSources.push(engagementLabelSourceId);
      newLabelLayers.push(engagementLabelLayerId);
    }
  }
};

const addAccommodationLabels = (
  selectedDay: number | null,
  map: React.RefObject<maptilersdk.Map>,
  isCompactView: boolean,
  newLabelSources: SourceId[],
  newLabelLayers: LayerId[],
  allAccommodations: AccommodationWithDays[],
) => {
  // Add accommodation labels
  if (map.current) {
    const accommodationLabelSourceId = 'accommodation-labels-source';
    const accommodationLabelLayerId = 'accommodation-labels-layer';

    map.current.addSource(accommodationLabelSourceId, {
      type: 'geojson',
      data: createAccommodationLabelGeoJSON(allAccommodations, selectedDay),
    });
    const acommLabelTextSize = isCompactView ? (selectedDay ? 12 : 8) : 16;

    map.current.addLayer({
      id: accommodationLabelLayerId,
      type: 'symbol',
      source: accommodationLabelSourceId,
      layout: {
        'text-field': [
          'format',
          ['get', 'dayRange'],
          {
            'font-scale': 0.8,
            'text-color': '#666666',
          },
          '\n',
          ['get', 'title'],
          {
            'font-scale': 1.2,
          },
        ],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': acommLabelTextSize,
        'text-offset': [0, 1.8],
        'text-radial-offset': 1.8,
        'text-variable-anchor': [
          'bottom',
          'top',
          'left',
          'right',
          'bottom-left',
          'bottom-right',
          'top-left',
          'top-right',
        ],
        'icon-text-fit': 'none',
        'text-allow-overlap': false,
        'text-ignore-placement': false,
        'icon-allow-overlap': false,
        'icon-ignore-placement': false,
        'icon-padding': 15,
        'symbol-sort-key': 3,
        'symbol-placement': 'point',
        'symbol-spacing': 250,
        'symbol-avoid-edges': true,
      },
      paint: {
        'text-color': '#333',
        'text-halo-color': 'rgba(225, 225, 225, 0.4)',
        'text-halo-width': 20,
        'text-halo-blur': 200,
      },
    });

    newLabelSources.push(accommodationLabelSourceId);
    newLabelLayers.push(accommodationLabelLayerId);
  }
};

const addEngagementMarkers = (
  engagements: EngagementWithDayNumber[],
  selectedDay: number | null,
  map: React.RefObject<maptilersdk.Map>,
  newLabelSources: SourceId[],
  newLabelLayers: LayerId[],
  bounds: maptilersdk.LngLatBounds,
) => {
  // Add engagement markers (inserted below accommodation markers using beforeId)
  if (map.current) {
    const engagementSourceId = 'engagement-markers-source';
    const engagementLayerId = 'engagement-markers-layer';
    const engagementCollisionLayerId = 'engagement-collision-layer';

    // Add the GeoJSON source
    map.current.addSource(engagementSourceId, {
      type: 'geojson',
      data: createEngagementMarkersGeoJSON(engagements, selectedDay),
    });

    // Add collision detection layer only for engagements on the selected day (when a day is selected)
    if (selectedDay) {
      map.current.addLayer(
        {
          id: engagementCollisionLayerId,
          type: 'symbol',
          source: engagementSourceId,
          filter: ['==', ['get', 'isSelectedDay'], true], // Only apply to selected day engagements
          paint: {
            'text-opacity': 0, // Hidden text for collision detection
          },
          layout: {
            'text-field': '0', // Use a single character for collision detection
            'text-size': 12, // Size that roughly matches the circle marker
            'text-offset': [0, 0], // Center the text on the marker
            'text-allow-overlap': false,
            'text-ignore-placement': false,
            'symbol-z-order': 'source',
            'symbol-sort-key': 1,
          },
        },
        'accommodation-collision-layer',
      ); // Insert before accommodation collision layer
      newLabelLayers.push(engagementCollisionLayerId);
    }

    // Add visible marker layer (insert before accommodation markers)
    map.current.addLayer(
      {
        id: engagementLayerId,
        type: 'circle',
        source: engagementSourceId,
        paint: {
          'circle-radius': 6,
          'circle-color': ['case', ['get', 'isSelectedDay'], '#5A5BBB', '#AAA8CF'],
        },
      },
      'accommodation-markers-layer',
    ); // Insert before accommodation marker layer

    newLabelSources.push(engagementSourceId);
    newLabelLayers.push(engagementLayerId);

    // Update bounds
    engagements.forEach((engagement) => {
      if (
        engagement.startMapLocation?.longitude &&
        engagement.startMapLocation?.latitude
      ) {
        bounds.extend([
          parseFloat(engagement.startMapLocation.longitude.toString()),
          parseFloat(engagement.startMapLocation.latitude.toString()),
        ]);
      }
    });
  }
};

const addPersistentAccommodationMarkers = (
  allAccommodations: AccommodationWithDays[],
  map: React.RefObject<maptilersdk.Map>,
  newLineSources: SourceId[],
  newLineLayers: LayerId[],
  bounds: maptilersdk.LngLatBounds,
) => {
  // Add persistent accommodation markers (no flickering, always on top)
  if (map.current) {
    const accommodationSourceId = 'accommodation-markers-source';
    const accommodationLayerId = 'accommodation-markers-layer';
    const accommodationCollisionLayerId = 'accommodation-collision-layer';

    // Check if source already exists, if not add it
    if (!map.current.getSource(accommodationSourceId)) {
      map.current.addSource(accommodationSourceId, {
        type: 'geojson',
        data: createAccommodationMarkersGeoJSON(allAccommodations, null),
      });
    }

    // Check if collision layer already exists, if not add it
    if (!map.current.getLayer(accommodationCollisionLayerId)) {
      map.current.addLayer({
        id: accommodationCollisionLayerId,
        type: 'symbol',
        source: accommodationSourceId,
        paint: {
          'text-opacity': 0, // Hidden text for collision detection
        },
        layout: {
          'text-field': '0', // Use a single character for collision detection
          'text-size': 16, // Size that roughly matches the circle marker
          'text-offset': [0, 0], // Center the text on the marker
          'text-allow-overlap': false,
          'text-ignore-placement': false,
          'symbol-sort-key': 2,
        },
      });
    }

    // Check if marker layer already exists, if not add it
    if (!map.current.getLayer(accommodationLayerId)) {
      map.current.addLayer({
        id: accommodationLayerId,
        type: 'circle',
        source: accommodationSourceId,
        paint: {
          'circle-radius': 8,
          'circle-color': '#5A5BBB',
        },
      });
    }

    newLineSources.push(accommodationSourceId);
    newLineLayers.push(accommodationCollisionLayerId);
    newLineLayers.push(accommodationLayerId);

    // Update bounds
    allAccommodations.forEach((accommodation) => {
      if (accommodation.location?.longitude && accommodation.location?.latitude) {
        bounds.extend([
          parseFloat(accommodation.location.longitude.toString()),
          parseFloat(accommodation.location.latitude.toString()),
        ]);
      }
    });
  }
};
